import React, { useEffect, useState } from "react";

import CommonApp from "./common/components/AuthApp";

import { Switch, Route } from "react-router-dom";

import BillDataPage from "./pages/Reports/BillData";
import BillPage from "./pages/BillPage";
import BrokenPromisesPage from "./pages/Reports/BrokenPromises";
import CompaniesLinkPage from "./pages/Link/CompaniesLinkPage";
import CompaniesPage from "./pages/Manage/CompaniesPage";
import CompanySpecialPricingPage from "./pages/Manage/CompanySpecialPricingPage";
import CompanyPage from "./pages/Manage/CompanyPage";
import CompaniesSyncPage from "./pages/Sync/CompaniesSyncPage";
import SystemsSyncPage from "./pages/Sync/SystemsSyncPage";
import IndexPage from "./pages/IndexPage";
import MendedPromisesPage from "./pages/Reports/MendedPromises";
import PastInvoicesSyncPage from "./pages/Sync/InvoicesSyncPage";
import PCSLicensePage from "./pages/Manage/PCSLicensePage";
import PCSLicensesPage from "./pages/Manage/PCSLicensesPage";
import SaleItemsPage from "./pages/Reports/SaleItemsPage";
import SaleItemsSyncPage from "./pages/Sync/SaleItemsSyncPage";
import SaleOrderSyncPage from "./pages/Sync/SaleOrderSyncPage";
import SaleOrderNewPage from "./pages/SaleOrderNewPage";
import SaleOrderPackPage from "./pages/SaleOrderPackPage";
import SaleOrderUpgradesPage from "./pages/SaleOrderUpgradesPage";
import SaleOrderPage from "./pages/SaleOrderPage";
import SaleOrdersPage from "./pages/SaleOrdersPage";
import SalesRepsPage from "./pages/SalesRepsPage";
import SalesRepSyncPage from "./pages/Sync/SalesRepSyncPage";
import UpgradesProgramPage from "./pages/Reports/UpgradesProgramPage";
import ItemSalesReportPage from "./pages/Reports/ItemSalesReportPage";

import VPNPage from "./pages/Manage/VPNPage";

import api from "./common/lib/api";
import { filterMenu } from "./menuConfig";

// Menu items with no `systems` field are universal (shown on every backend).
// Items with a `systems` array are shown only when at least one of those system
// ids is present in the backend's configured systems[] (fetched from GET
// /systems). System ids: atlas, rlxx, rlau, rlnz, globe (AU), atlas, rluk (UK).
const MENU = [
  {
    section: "Manage",
    items: [
      {
        label: "Sale Orders",
        href: "/sale-orders",
        icon: "shopping-cart",
        access: ["accounting_saleOrder_view"],
      },
      {
        label: "Companies Linkage",
        href: "/manage/companies-linkage",
        icon: "office",
        access: ["accounting_link_company"],
      },
      {
        label: "PCS Licenses",
        href: "/manage/pcs-licenses",
        icon: "id-number",
        access: ["accounting_pcsLicense_view"],
      },
      {
        label: "Companies",
        href: "/companies",
        icon: "office",
        access: ["accounting_companies_view"],
      },
      {
        label: "VPN Services",
        href: "/manage/vpn",
        icon: "shield",
        access: ["accounting_bill_vpn"],
      },
    ],
  },
  {
    section: "EOM Bill",
    items: [
      {
        label: "RLXX [@09:00]",
        href: "/bill/rlxx/now",
        icon: "calendar",
        access: ["accounting_bill_view"],
        systems: ["rlxx"]
      },
      {
        label: "RLNZ [@10:00]",
        href: "/bill/rlnz/now",
        icon: "calendar",
        access: ["accounting_bill_view"],
        systems: ["rlnz"]
      },
      {
        label: "RLAU [@12:00]",
        href: "/bill/rlau/now",
        icon: "calendar",
        access: ["accounting_bill_view"],
        systems: ["rlau"]
      },
      {
        label: "RLUK [@12:00]",
        href: "/bill/rluk/now",
        icon: "calendar",
        access: ["accounting_bill_view"],
        systems: ["rluk"]
      },
      {
        label: "Atlas [@14:00]",
        href: "/bill/atlas/now",
        icon: "calendar",
        access: ["accounting_bill_view"],
        systems: ["atlas"]
      },
      {
        label: "Globe [@16:00]",
        href: "/bill/globe/now",
        icon: "calendar",
        access: ["accounting_bill_view"],
        systems: ["globe"]
      },
    ],
  },
  {
    section: "Reports",
    items: [
      {
        label: "Item Sales",
        href: "/reports/item-sales",
        icon: "chart",
        access: ["accounting_report_itemSales"],
      },
      {
        label: "Bill Data",
        href: "/reports/bill-data",
        icon: "database",
        access: ["accounting_bill_viewBillData"],
      },
      {
        label: "Sale Items",
        href: "/reports/sale-items",
        icon: "shopping-cart",
        access: ["accounting_link_items"],
      },
      {
        label: "T&C Breaches",
        href: "/reports/broken-promises",
        icon: "issue",
        access: ["accounting_bill_termsInvoices"],
      },
      {
        label: "T&C Credits",
        href: "/reports/mended-promises",
        icon: "issue-closed",
        access: ["accounting_bill_termsInvoices"],
      },
      {
        label: "Upgrade Program",
        href: "/reports/upgrade-program",
        icon: "double-chevron-up",
        access: ["accounting_bill_upgrades"],
      },
      {
        label: "Sales Reps",
        href: "/reports/sales-reps",
        icon: "people",
        access: ["accounting_salesRep_view"],
      },
    ],
  },
  {
    section: "Synchronise",
    items: [
      {
        label: "Companies",
        href: "/sync/companies",
        icon: "refresh",
        access: ["accounting_sync_company"],
      },
      {
        label: "Sale Items",
        href: "/sync/sale-items",
        icon: "refresh",
        access: ["accounting_sync_items"],
      },
      {
        label: "Systems",
        href: "/sync/systems",
        icon: "refresh",
        access: ["accounting_sync_saleOrders"],
      },
      {
        label: "Invoices",
        href: "/sync/invoices",
        icon: "refresh",
        access: ["accounting_sync_invoices"],
      },
      {
        label: "Sales Reps",
        href: "/sync/sales-reps",
        icon: "refresh",
        access: ["accounting_sync_salesRep"],
      },
      {
        label: "Sale Orders",
        href: "/sync/sale-orders",
        icon: "refresh",
        access: ["accounting_sync_saleOrders"],
      },
    ],
  },
];

function App() {
  // null = not yet fetched -> filterMenu shows every item (see menuConfig).
  const [systems, setSystems] = useState(null);

  useEffect(() => {
    document.title = "Accounting Portal";
  }, []);

  useEffect(() => {
    api
      .get("/systems")
      .then((res) => setSystems(res.data.data))
      .catch(() => setSystems(null));
  }, []);

  return (
    <CommonApp menu={filterMenu(MENU, systems)}>
      <Switch>
        {/* Sync */}
        <Route path="/sync/companies" component={CompaniesSyncPage} />
        <Route path="/sync/systems" component={SystemsSyncPage} />
        <Route path="/sync/sale-items" component={SaleItemsSyncPage} />
        <Route path="/sync/sales-reps" component={SalesRepSyncPage} />
        <Route path="/sync/sale-orders" component={SaleOrderSyncPage} />
        <Route path="/sync/invoices" component={PastInvoicesSyncPage} />

        <Route path="/reports/item-sales" component={ItemSalesReportPage} />
        <Route
          path="/reports/bill-data/:system/:period/:companyId"
          component={BillDataPage}
        />
        <Route path="/reports/bill-data" component={BillDataPage} />
        <Route path="/reports/sale-items" component={SaleItemsPage} />

        <Route path="/manage/companies-linkage" component={CompaniesLinkPage} />
        <Route
          path="/manage/pcs-license/:licenseEmail"
          component={PCSLicensePage}
        />
        <Route path="/manage/pcs-licenses" component={PCSLicensesPage} />

        <Route path="/sale-order-new/:system" component={SaleOrderNewPage} />
        <Route
          path="/sale-order/:saleOrderId/pack"
          component={SaleOrderPackPage}
        />
        <Route
          path="/sale-order/:saleOrderId/upgrade-tracker"
          component={SaleOrderUpgradesPage}
        />
        <Route path="/sale-order/:saleOrderId" component={SaleOrderPage} />
        <Route path="/sale-orders" component={SaleOrdersPage} />
        <Route
          path="/companies/:companyId/special-pricing"
          component={CompanySpecialPricingPage}
        />
        <Route path="/companies/:companyId" component={CompanyPage} />
        <Route path="/companies" component={CompaniesPage} />

        <Route path="/reports/sales-reps" component={SalesRepsPage} />

        <Route path="/bill/:system/:period" component={BillPage} />
        <Route path="/bill/:system" component={BillPage} />

        <Route path="/reports/broken-promises" component={BrokenPromisesPage} />
        <Route path="/reports/mended-promises" component={MendedPromisesPage} />
        <Route
          path="/reports/upgrade-program"
          component={UpgradesProgramPage}
        />

        <Route path="/manage/vpn" component={VPNPage} />
        <Route path="/" component={IndexPage} />
        <Route component={IndexPage} />
      </Switch>
    </CommonApp>
  );
}

export default App;
